import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, KeyRound, Link2, LockKeyhole, Plus, QrCode, ShieldCheck, Users } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../api/client.ts';
import { keys, useMe } from '../api/hooks.ts';
import { emptyQuickPerson, QuickPersonFields, quickToApi } from '../components/PersonBits.tsx';
import { Badge, Button, Dialog, ErrorNote, Input, Textarea } from '../components/ui/index.tsx';
import { t } from '../i18n/index.ts';
import { Brand } from '../layout/Brand.tsx';
import { ROLE_LABEL } from '../lib/format.ts';

export function TreesPage() {
  const me = useMe().data!;
  const [creating, setCreating] = useState(false);
  const listed = me.listedTrees ?? [];
  const noTrees = !me.trees.length;
  return (
    <div className="min-h-dvh">
      <header className="mx-auto flex max-w-5xl items-center justify-between px-5 py-5">
        <Brand to="/arboles" />
        <span className="flex items-center gap-2">
          {me.user.isPlatformAdmin && <Badge tone="ochre">{t('Administrador de la plataforma')}</Badge>}
          <Link to="/cuenta" className="text-[14.5px] text-ink-soft hover:text-ink">{me.user.displayName}</Link>
        </span>
      </header>
      <main className="mx-auto max-w-5xl px-5 pb-20 pt-6">
        <h1 className="text-[34px]">{noTrees ? t('Hola, {name}', { name: me.user.displayName }) : t('Tus árboles familiares')}</h1>
        <p className="mt-2 max-w-xl text-[15.5px] text-ink-soft">
          {noTrees
            ? t('Tu cuenta está lista. Para ver el árbol de tu familia te falta un paso: la invitación.')
            : t('Cada árbol es privado: solo lo ven los familiares invitados. Para unirte a un árbol existente escanea el QR que te compartan.')}
        </p>
        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {me.trees.map((tree) => (
            <li key={tree.id}>
              <Link to={`/t/${tree.id}`} className="group flex h-full flex-col overflow-hidden rounded-3xl border border-line bg-surface p-5 transition-colors hover:border-ink-faint">
                <div className="tocapu -mx-5 -mt-5 mb-5 opacity-60 transition-opacity group-hover:opacity-100" />
                <h2 className="text-[21px]">{tree.name}</h2>
                {tree.description && <p className="mt-1 line-clamp-2 text-[14.5px] text-ink-soft">{tree.description}</p>}
                <p className="mt-auto flex items-center gap-2 pt-5 text-[13.5px] text-ink-faint">
                  <Users className="size-4" aria-hidden /> {t('{count} personas', { count: tree.personsCount })} · {ROLE_LABEL[tree.role]}
                </p>
              </Link>
            </li>
          ))}
          {listed.map((tree) => (
            <li key={tree.name} className={noTrees ? 'sm:col-span-2 lg:col-span-2' : undefined}>
              <LockedTree name={tree.name} wide={noTrees} />
            </li>
          ))}
          {me.allowTreeCreation !== false && (
            <li>
              <button
                onClick={() => setCreating(true)}
                className="flex h-full min-h-40 w-full flex-col items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-line text-ink-soft transition-colors hover:border-ink-faint hover:text-ink"
              >
                <Plus className="size-6" aria-hidden />
                <span className="font-medium">{t('Empezar un árbol nuevo')}</span>
              </button>
            </li>
          )}
        </ul>
        {noTrees && !listed.length && <InviteSteps />}
        {(noTrees || listed.length > 0) && <PasteInvitation />}
        {me.allowTreeCreation === false && (
          <p className="mt-8 flex max-w-xl items-start gap-2 text-[14px] text-ink-faint">
            <ShieldCheck className="mt-0.5 size-4 shrink-0" aria-hidden />
            {t('Por seguridad, a los árboles se entra solo por invitación: así los datos de tu familia los ve únicamente tu familia.')}
          </p>
        )}
      </main>
      <CreateTreeDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

/**
 * Árbol que su dueño anuncia: se ve el nombre, con candado, y cómo entrar. Quien recién se registra sabe así que llegó
 * al lugar correcto aunque todavía no tenga acceso.
 */
function LockedTree({ name, wide }: { name: string; wide: boolean }) {
  return (
    <section aria-label={t('{name}: necesitas una invitación', { name })} className="flex h-full flex-col overflow-hidden rounded-3xl border border-line bg-surface p-5">
      <div className="tocapu -mx-5 -mt-5 mb-5 opacity-40" />
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-[21px]">{name}</h2>
        <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-surface-2 px-2.5 py-1 text-[12.5px] font-medium text-ink-soft">
          <LockKeyhole className="size-3.5" aria-hidden /> {t('Con invitación')}
        </span>
      </div>
      <p className="mt-2 text-[14.5px] text-ink-soft">{t('Para entrar necesitas el enlace o el código QR de invitación del árbol.')}</p>
      {wide && <InviteSteps compact />}
    </section>
  );
}

/** Cómo conseguir y usar la invitación, en pasos concretos. */
function InviteSteps({ compact }: { compact?: boolean }) {
  const steps = [
    { icon: Users, text: t('Pídele el enlace o el código QR de invitación a quien administra el árbol o a un familiar que ya esté en él.') },
    { icon: QrCode, text: t('Abre el enlace o escanea el QR con la cámara del teléfono, con esta misma cuenta.') },
    { icon: KeyRound, text: t('Indica quién eres y quiénes son tus padres: así te ubicamos en el árbol.') },
  ];
  return (
    <ol className={compact ? 'mt-4 flex flex-col gap-3 border-t border-line pt-4' : 'mt-8 flex max-w-xl flex-col gap-3 rounded-3xl border border-line bg-surface p-5'}>
      {steps.map((s, i) => (
        <li key={i} className="flex items-start gap-3 text-[14.5px]">
          <span className="grid size-7 shrink-0 place-items-center rounded-full bg-pine text-[13px] font-semibold text-on-pine" aria-hidden>{i + 1}</span>
          <span className="pt-0.5">{s.text}</span>
        </li>
      ))}
    </ol>
  );
}

/** Para quien recibió el enlace por mensaje y lo abre en otro equipo (un computador o la TV): pegarlo aquí basta. */
function PasteInvitation() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const token = /(?:\/unirse\/)?([A-Za-z0-9_-]{40,64})\/?\s*$/.exec(value.trim())?.[1];
    if (!token) return setError(t('Ese no parece un enlace de invitación. Cópialo completo, tal como te llegó.'));
    navigate(`/unirse/${token}`);
  };
  return (
    <form onSubmit={submit} className="mt-6 flex max-w-xl flex-col gap-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <Input
            label={t('¿Ya tienes el enlace? Pégalo aquí')}
            placeholder="https://gallarday.net/unirse/…"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
            error={error}
            autoComplete="off"
            inputMode="url"
          />
        </div>
        <Button type="submit" variant="secondary" icon={<Link2 className="size-4" aria-hidden />} disabled={!value.trim()}>{t('Abrir invitación')}</Button>
      </div>
    </form>
  );
}

function CreateTreeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [me, setMe] = useState(emptyQuickPerson());
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (step === 1) return setStep(2);
    setLoading(true);
    setError(null);
    try {
      const q = quickToApi(me);
      const created = await api.post<{ id: string }>('/api/trees', {
        name,
        description,
        me: { givenNames: q.givenNames, paternalSurname: q.paternalSurname, maternalSurname: q.maternalSurname, sex: q.sex, birth: q.birthYear ? { date: { year: q.birthYear } } : null },
      });
      await qc.invalidateQueries({ queryKey: keys.me });
      navigate(`/t/${created.id}`);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} title={step === 1 ? t('Nuevo árbol familiar') : t('¿Quién eres?')}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        {step === 1 ? (
          <>
            <Input label={t('Nombre del árbol')} required autoFocus placeholder={t('Ej. Clan Gallarday')} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
            <Textarea label={t('Descripción (opcional)')} value={description} maxLength={2000} onChange={(e) => setDescription(e.target.value)} placeholder={t('Desde Huaraz, Áncash, en el siglo XIX…')} />
          </>
        ) : (
          <>
            <p className="text-[15px] text-ink-soft">{t('Serás la primera persona del árbol. Desde ti agregarás a tus padres, abuelos y el resto de la familia.')}</p>
            <QuickPersonFields value={me} onChange={setMe} autoFocus />
          </>
        )}
        <ErrorNote error={error} />
        <div className="mt-2 flex justify-between gap-2">
          {step === 2 ? (
            <Button variant="ghost" icon={<ArrowLeft className="size-4" />} onClick={() => setStep(1)}>{t('Atrás')}</Button>
          ) : <span />}
          <Button type="submit" loading={loading}>{step === 1 ? t('Continuar') : t('Crear árbol')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
